import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Edit2, 
  Trash2, 
  Settings2,
  Filter,
  CheckCircle2,
  AlertCircle,
  ChevronRight,
  Package
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useApiClient } from "@/hooks/use-api-client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface Plan {
  id: string;
  name: string;
  price_per_month: number;
}

interface PlanFeature {
  id: string;
  plan_id: string;
  name: string;
  description: string | null;
  created_at: string;
}

export default function AdminPlanFeatures() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [features, setFeatures] = useState<PlanFeature[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPlanFilter, setSelectedPlanFilter] = useState<string>("all");
  
  // Modal states
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  
  const [currentFeature, setCurrentFeature] = useState<Partial<PlanFeature>>({});
  const apiClient = useApiClient();

  const fetchData = async () => {
    try {
      setLoading(true);
      const [plansRes, featuresRes] = await Promise.all([
        (apiClient as any).get("/api/v1/admin/plans"),
        (apiClient as any).get("/api/v1/admin/plan-features")
      ]);
      setPlans(plansRes?.data || []);
      setFeatures(featuresRes?.data || []);
    } catch (error) {
      console.error("Admin error:", error);
      toast.error("Error al cargar datos administrativos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreate = async () => {
    if (!currentFeature.plan_id || !currentFeature.name) {
      toast.error("Plan y nombre son obligatorios");
      return;
    }
    try {
      await (apiClient as any).post("/api/v1/admin/plan-features", currentFeature);
      toast.success("Característica creada exitosamente");
      setIsCreateOpen(false);
      setCurrentFeature({});
      fetchData();
    } catch (error) {
      toast.error("Error al crear característica");
    }
  };

  const handleUpdate = async () => {
    if (!currentFeature.id) return;
    try {
      await (apiClient as any).put(`/api/v1/admin/plan-features/${currentFeature.id}`, currentFeature);
      toast.success("Característica actualizada");
      setIsEditOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Error al actualizar");
    }
  };

  const handleDelete = async () => {
    if (!currentFeature.id) return;
    try {
      await (apiClient as any).delete(`/api/v1/admin/plan-features/${currentFeature.id}`);
      toast.success("Característica eliminada");
      setIsDeleteOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const filteredFeatures = features.filter(f => {
    const matchesSearch = f.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                         (f.description?.toLowerCase() || "").includes(searchQuery.toLowerCase());
    const matchesPlan = selectedPlanFilter === "all" || f.plan_id === selectedPlanFilter;
    return matchesSearch && matchesPlan;
  });

  const getPlanName = (planId: string) => {
    return plans.find(p => p.id === planId)?.name || "Desconocido";
  };

  return (
    <div className="p-8 space-y-8 bg-[#fdfdfd] min-h-screen">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight flex items-center gap-3">
            <Settings2 className="h-8 w-8 text-indigo-600" />
            Gestión de <span className="text-indigo-600">Planes</span>
          </h1>
          <p className="text-slate-500 font-medium mt-1">Configuración técnica de características y beneficios por plan.</p>
        </div>
        <Button 
          onClick={() => {
            setCurrentFeature({});
            setIsCreateOpen(true);
          }}
          className="rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg shadow-indigo-200 gap-2 font-bold"
        >
          <Plus className="h-4 w-4" /> Nueva Característica
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input 
            className="pl-10 rounded-xl border-slate-200 bg-white shadow-sm" 
            placeholder="Buscar por nombre o descripción..." 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Select value={selectedPlanFilter} onValueChange={setSelectedPlanFilter}>
          <SelectTrigger className="w-[200px] rounded-xl border-slate-200 bg-white shadow-sm font-medium">
            <Filter className="h-4 w-4 mr-2 text-slate-400" />
            <SelectValue placeholder="Filtrar por Plan" />
          </SelectTrigger>
          <SelectContent className="rounded-xl border-slate-200">
            <SelectItem value="all">Todos los planes</SelectItem>
            {plans.map(p => (
              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Features Table */}
      <Card className="rounded-[2rem] border-slate-200 shadow-sm overflow-hidden">
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-slate-50/50">
              <TableRow className="border-slate-100">
                <TableHead className="font-black text-slate-500 uppercase text-[10px] tracking-widest pl-6">Característica</TableHead>
                <TableHead className="font-black text-slate-500 uppercase text-[10px] tracking-widest">Plan</TableHead>
                <TableHead className="font-black text-slate-500 uppercase text-[10px] tracking-widest">Descripción</TableHead>
                <TableHead className="font-black text-slate-500 uppercase text-[10px] tracking-widest text-right pr-6">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i} className="animate-pulse">
                    <TableCell colSpan={4} className="h-16 bg-slate-50/20" />
                  </TableRow>
                ))
              ) : filteredFeatures.length > 0 ? (
                filteredFeatures.map((feature) => (
                  <TableRow key={feature.id} className="hover:bg-slate-50/50 border-slate-100 group transition-colors">
                    <TableCell className="pl-6 py-4">
                      <div className="font-bold text-slate-900">{feature.name}</div>
                      <div className="text-[10px] text-slate-400 font-mono tracking-tighter">ID: {feature.id.slice(0, 8)}</div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-white border-slate-200 text-slate-600 font-bold text-[10px] uppercase">
                        {getPlanName(feature.plan_id)}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-md">
                      <p className="text-sm text-slate-500 line-clamp-1">{feature.description || "—"}</p>
                    </TableCell>
                    <TableCell className="text-right pr-6">
                      <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-indigo-50"
                          onClick={() => {
                            setCurrentFeature(feature);
                            setIsEditOpen(true);
                          }}
                        >
                          <Edit2 className="h-3.5 w-3.5" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                          onClick={() => {
                            setCurrentFeature(feature);
                            setIsDeleteOpen(true);
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} className="h-32 text-center text-slate-400 font-medium">
                    No se encontraron características.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Modal */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="rounded-[2rem] border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black">Nueva Característica</DialogTitle>
            <DialogDescription>Añade una funcionalidad al catálogo de planes.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-xs font-black uppercase text-slate-500">Plan Asociado</Label>
              <Select 
                onValueChange={(val) => setCurrentFeature({...currentFeature, plan_id: val})}
              >
                <SelectTrigger className="rounded-xl border-slate-200">
                  <SelectValue placeholder="Seleccionar Plan" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  {plans.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-black uppercase text-slate-500">Nombre</Label>
              <Input 
                className="rounded-xl border-slate-200" 
                placeholder="Ej: Análisis de Riesgos Ilimitado"
                value={currentFeature.name || ""}
                onChange={(e) => setCurrentFeature({...currentFeature, name: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-black uppercase text-slate-500">Descripción</Label>
              <Textarea 
                className="rounded-xl border-slate-200 min-h-[100px]" 
                placeholder="Describe brevemente el beneficio..."
                value={currentFeature.description || ""}
                onChange={(e) => setCurrentFeature({...currentFeature, description: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setIsCreateOpen(false)} className="rounded-xl font-bold">Cancelar</Button>
            <Button onClick={handleCreate} className="rounded-xl bg-indigo-600 font-bold">Crear</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="rounded-[2rem] border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black">Editar Característica</DialogTitle>
            <DialogDescription>Actualiza los detalles de este beneficio.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
             <div className="space-y-2">
              <Label className="text-xs font-black uppercase text-slate-500">Plan</Label>
              <Select 
                value={currentFeature.plan_id}
                onValueChange={(val) => setCurrentFeature({...currentFeature, plan_id: val})}
              >
                <SelectTrigger className="rounded-xl border-slate-200">
                  <SelectValue placeholder="Seleccionar Plan" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  {plans.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-black uppercase text-slate-500">Nombre</Label>
              <Input 
                className="rounded-xl border-slate-200" 
                value={currentFeature.name || ""}
                onChange={(e) => setCurrentFeature({...currentFeature, name: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-black uppercase text-slate-500">Descripción</Label>
              <Textarea 
                className="rounded-xl border-slate-200 min-h-[100px]" 
                value={currentFeature.description || ""}
                onChange={(e) => setCurrentFeature({...currentFeature, description: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setIsEditOpen(false)} className="rounded-xl font-bold">Cancelar</Button>
            <Button onClick={handleUpdate} className="rounded-xl bg-indigo-600 font-bold">Guardar Cambios</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Modal */}
      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <DialogContent className="rounded-[2rem] border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black text-rose-600">¿Eliminar característica?</DialogTitle>
            <DialogDescription>
              Esta acción no se puede deshacer. Se eliminará permanentemente de este plan.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 flex items-center gap-4 p-4 bg-rose-50 rounded-2xl border border-rose-100">
             <AlertCircle className="h-8 w-8 text-rose-600" />
             <div>
               <p className="text-sm font-bold text-rose-900">{currentFeature.name}</p>
               <p className="text-xs text-rose-700">Plan: {getPlanName(currentFeature.plan_id || "")}</p>
             </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setIsDeleteOpen(false)} className="rounded-xl font-bold">Cancelar</Button>
            <Button onClick={handleDelete} className="rounded-xl bg-rose-600 text-white font-bold">Eliminar Definitivamente</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
